import React from 'react';
import { useState } from 'react';
import { AddressMethod, AddressSearch, AddressSearchValue } from '@qbe/dfc-smart-ui';
import { AddressList } from '@qbe/dfc-services';
import { useLazyGetAddressesQuery, useStructuredAddressLookupMutation } from 'store';
import { ADDRESS_FIELD_MAPS, AddressType } from './addressHelpers';
import { Box } from '@qbe/ads-core';
import { useAddressFieldOverrides } from 'hooks/useAddressFieldOverrides';

export interface CmsAddressSearchProps {
  /** Which address block this component represents */
  type: AddressType;
  required?: boolean;
  /** Current address object from form state */
  defaultAddress?: AddressList;
  /** Current manual address values from form state */
  defaultManualAddress?: AddressSearchValue;
  /** Whether the address is currently in manual entry mode */
  isManualAddress?: boolean;
  /** Callback when address changes - parent should update form values */
  onAddressChange: (params: {
    addressList: AddressList | null;
    structured: AddressSearchValue | null;
    method: AddressMethod;
    type: AddressType;
  }) => void;
}

const CmsAddressSearch = ({
  type,
  required = false,
  defaultAddress,
  defaultManualAddress,
  isManualAddress = false,
  onAddressChange,
}: CmsAddressSearchProps) => {
  const [addressSearchKey, setAddressSearchKey] = useState(0);

  // Import Redux hooks directly for address API calls
  const addressApiHooks = {
    useLazyGetAddressesQuery,
    useStructuredAddressLookupMutation,
  };

  // Get localized field configuration
  const fieldOverrides = useAddressFieldOverrides(type);

  const handleAddressChange = (
    addressList: AddressList | null,
    structured: AddressSearchValue | null,
    _fieldName: string,
    method: AddressMethod
  ) => {
    // Notify parent to update form values
    onAddressChange({ addressList, structured, method, type });

    // Re-mount AddressSearch when switching from lookup to manual mode for the first time.
    // This resets the internal component state to prevent stale lookup data from interfering
    // with manual entry. We only do this once (!isManualAddress check) to avoid resetting
    // the form while the user is actively typing in manual fields.
    if (method === AddressMethod.Manual && structured === null && !isManualAddress) {
      setAddressSearchKey((k) => k + 1);
    }
  };

  const handleAddressesError = () => {
    console.error('Address lookup service error occurred');
  };

  return (
    <Box maxWidth={400} width="100%">
      <AddressSearch
        key={addressSearchKey}
        fieldName={ADDRESS_FIELD_MAPS[type].fieldName}
        fieldOverrides={fieldOverrides}
        defaultAddress={defaultAddress}
        defaultManualAddress={defaultManualAddress}
        addressApiHooks={addressApiHooks}
        onChange={handleAddressChange}
        onAddressesError={handleAddressesError}
        required={required}
        showManualCountry
        validatePostcodeState
      />
    </Box>
  );
};

export default CmsAddressSearch;
